import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { Tag, Wine, Package, Send, AlertCircle } from "lucide-react";

export default function B2BSellLot() {
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    product_type: "wine",
    title: "",
    description: "",
    image_url: "",
    quantity: 600,
    pallet_size: 600,
    packaging: "Bancale 600 bottiglie · cartoni da 6",
    price_per_unit: 9.5,
    min_order_qty: 60,
    vintage: 2022,
    region: "",
    grape: "",
    expiry_date: "",
    location: "",
    available_until: "",
  });

  const onChange = (k) => (e) => {
    const v = e.target.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.product_type === "foodbox") {
        delete payload.vintage; delete payload.grape; delete payload.region;
      } else {
        delete payload.expiry_date;
      }
      const r = await api.post("/b2b/lots", payload);
      toast.success("Lotto inviato. In attesa di approvazione Super Admin.");
      nav(`/b2b/lots/mine`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio lotto");
    } finally {
      setSubmitting(false);
    }
  };

  const totalValue = (form.price_per_unit || 0) * (form.quantity || 0);

  return (
    <div data-testid="b2b-sell-lot-page" className="container-xl py-10 max-w-4xl">
      <div className="flex items-center gap-3 mb-2">
        <Tag className="h-6 w-6 text-[hsl(var(--accent))]" />
        <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] font-semibold">Vendi lotto</div>
      </div>
      <h1 className="editorial-heading text-4xl md:text-5xl mb-2">Pubblica un lotto in vendita</h1>
      <p className="text-muted-foreground mb-6">Inserisci i dettagli del lotto. Sarà visibile dopo l'approvazione del Super Admin.</p>

      <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-6 flex gap-3" data-testid="approval-notice">
        <AlertCircle className="h-5 w-5 text-amber-700 shrink-0" />
        <div className="text-sm text-amber-900">
          <strong>Workflow di approvazione:</strong> il lotto resta in stato <em>"in attesa"</em> fino alla validazione del Super Admin.
          Solo dopo l'approvazione diventa visibile e acquistabile dagli altri rivenditori.
        </div>
      </div>

      <form onSubmit={submit} className="space-y-6">
        {/* TYPE TOGGLE */}
        <div className="bg-card border border-border rounded-sm p-4">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Tipo prodotto</div>
          <div className="flex gap-3">
            <ProductTypeBtn active={form.product_type === "wine"} onClick={() => setForm({ ...form, product_type: "wine" })} icon={Wine} label="Vino" testid="lot-type-wine" />
            <ProductTypeBtn active={form.product_type === "foodbox"} onClick={() => setForm({ ...form, product_type: "foodbox" })} icon={Package} label="Food Box" testid="lot-type-foodbox" />
          </div>
        </div>

        {/* DETAILS */}
        <div className="bg-card border border-border rounded-sm p-6 space-y-4">
          <h2 className="text-sm uppercase tracking-[0.2em] text-muted-foreground">Dettagli lotto</h2>
          <Input label="Titolo *" value={form.title} onChange={onChange("title")} required maxLength={200} placeholder={form.product_type === "wine" ? "es. Chianti Classico DOCG 2022 — bancale 600 bt" : "es. Box gourmet stagione autunno 2025 — 200 unità"} testid="lot-title" />
          <Textarea label="Descrizione *" value={form.description} onChange={onChange("description")} rows={4} required maxLength={2000} placeholder="Specifica origine, condizioni, eventuali certificazioni…" testid="lot-description" />
          <Input label="Immagine URL" value={form.image_url} onChange={onChange("image_url")} placeholder="https://..." />
          <div className="grid md:grid-cols-3 gap-4">
            <Input label="Quantità totale *" type="number" min={1} value={form.quantity} onChange={onChange("quantity")} required testid="lot-qty" />
            <Input label="Confezionamento" value={form.packaging} onChange={onChange("packaging")} placeholder="es. cartoni da 6 / bancale" />
            <Input label="Pezzi per pallet" type="number" min={1} value={form.pallet_size} onChange={onChange("pallet_size")} />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <Input label="Prezzo unitario (€) *" type="number" step="0.01" min={0.01} value={form.price_per_unit} onChange={onChange("price_per_unit")} required testid="lot-price" />
            <Input label="Quantità minima ordine *" type="number" min={1} value={form.min_order_qty} onChange={onChange("min_order_qty")} required />
            <div className="flex items-end">
              <div className="w-full px-3 py-2 bg-[hsl(var(--muted))] rounded-sm">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Valore totale lotto</div>
                <div className="text-lg font-bold text-[hsl(var(--accent))]" data-testid="lot-total-value">€ {totalValue.toLocaleString("it-IT", { maximumFractionDigits: 2 })}</div>
              </div>
            </div>
          </div>

          {form.product_type === "wine" && (
            <div className="grid md:grid-cols-3 gap-4 pt-4 border-t border-border">
              <Input label="Annata" type="number" min={1900} max={2100} value={form.vintage} onChange={onChange("vintage")} />
              <Input label="Regione/IGT/DOCG" value={form.region} onChange={onChange("region")} placeholder="es. Toscana DOCG" />
              <Input label="Vitigno" value={form.grape} onChange={onChange("grape")} placeholder="es. Sangiovese" />
            </div>
          )}
          {form.product_type === "foodbox" && (
            <div className="grid md:grid-cols-2 gap-4 pt-4 border-t border-border">
              <Input label="Scadenza" type="date" value={form.expiry_date} onChange={onChange("expiry_date")} />
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4 pt-4 border-t border-border">
            <Input label="Luogo / Magazzino" value={form.location} onChange={onChange("location")} placeholder="es. Verona" />
            <Input label="Offerta valida fino al" type="date" value={form.available_until} onChange={onChange("available_until")} />
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid="lot-submit"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[hsl(var(--primary))] text-white font-semibold rounded-sm hover:bg-[hsl(var(--primary))]/90 transition-colors disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
          {submitting ? "Invio in corso…" : "Invia lotto per approvazione"}
        </button>
      </form>
    </div>
  );
}

function ProductTypeBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border rounded-sm transition-colors ${
        active ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "bg-transparent border-border text-foreground hover:border-[hsl(var(--primary))]"
      }`}
    >
      <Icon className="h-5 w-5" />
      <span className="font-semibold">{label}</span>
    </button>
  );
}

function Input({ label, testid, ...rest }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">{label}</span>
      <input {...rest} data-testid={testid} className="w-full px-3 py-2 border border-border rounded-sm bg-background" />
    </label>
  );
}

function Textarea({ label, testid, ...rest }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">{label}</span>
      <textarea {...rest} data-testid={testid} className="w-full px-3 py-2 border border-border rounded-sm bg-background" />
    </label>
  );
}
